import { useGameStore } from '@/stores/game';
import type { BetslipItem } from '@/stores/betslip';
import type { RelatedGame } from '@/stores/game';

interface MatchingResult {
  market: any;
  marketGroup: any;
  outcome: any;
  gameDetail: any;
  validDraws: any[];
}

export function useRelatedGameMatching() {
  const gameStore = useGameStore();

  const findMatchingBet = async (
    relatedGame: RelatedGame,
    originalBet: BetslipItem
  ): Promise<MatchingResult> => {
    await gameStore.fetchGameDetails(relatedGame.slug, false);
    
    if (!gameStore.getBetSettings) {
      await gameStore.fetchGameSettings(false);
    }

    const relatedGameDetail = gameStore.getGameDetail;
    if (!relatedGameDetail) {
      throw new Error('Failed to load game details');
    }

    const validDraws = gameStore.getValidDraws || [];
    if (validDraws.length === 0) {
      throw new Error('No valid draws available for this game');
    }

    const marketGroups = gameStore.getMarketGroups || [];
    let matchingMarket = null;
    let matchingMarketGroup = null;
    let matchingOutcome = null;

    const originalMarketName = originalBet.market_name || '';
    const originalBetLabel = originalBet.label || '';
    const originalSpecifier = originalBet.specifier || null;
    const originalOdds = originalBet.odds;
    const originalPicks = originalBet.picks;

    const normalizeMarketName = (name: string) => {
      return name.toLowerCase().trim().replace(/\s+/g, ' ');
    };

    const normalizedOriginalMarketName = normalizeMarketName(originalMarketName);
    for (const group of marketGroups) {
      for (const market of group.markets) {
        const normalizedMarketName = normalizeMarketName(market.name);
        const normalizedMarketSlug = market.slug.toLowerCase();

        const marketNameMatches =
          normalizedMarketName === normalizedOriginalMarketName ||
          normalizedMarketSlug === normalizedOriginalMarketName.replace(/\s+/g, '-') ||
          normalizedMarketName.includes(normalizedOriginalMarketName) ||
          normalizedOriginalMarketName.includes(normalizedMarketName);

        if (marketNameMatches) {
          for (const outcome of market.outcomes) {
            let outcomeMatches = false;

            if (outcome.text && originalBetLabel) {
              const normalizedOutcomeText = outcome.text.toLowerCase().trim();
              const normalizedOriginalLabel = originalBetLabel.toLowerCase().trim();
              if (normalizedOutcomeText === normalizedOriginalLabel) {
                outcomeMatches = true;
              }
            }

            if (!outcomeMatches && originalSpecifier && outcome.specifier) {
              if (outcome.specifier === originalSpecifier) {
                outcomeMatches = true;
              }
            }

            if (!outcomeMatches && originalPicks && outcome.pick) {
              if (Number(outcome.pick) === Number(originalPicks)) {
                outcomeMatches = true;
              }
            }

            if (!outcomeMatches && originalOdds && outcome.odds) {
              const oddsDiff = Math.abs(Number(outcome.odds) - Number(originalOdds));
              if (oddsDiff < Math.max(0.01, Number(originalOdds) * 0.01)) {
                outcomeMatches = true;
              }
            }

            if (outcomeMatches) {
              matchingMarket = market;
              matchingMarketGroup = group;
              matchingOutcome = outcome;
              break;
            }
          }

          if (matchingOutcome) {
            break;
          }
        }
      }

      if (matchingOutcome) {
        break;
      }
    }

    if (!matchingMarket || !matchingMarketGroup) {
      const availableMarkets = marketGroups.flatMap(g => g.markets.map(m => m.name));
      throw new Error(
        `Matching market "${originalMarketName}" not found in related game "${relatedGame.name}". Available markets: ${availableMarkets.join(', ')}`
      );
    }

    if (!matchingOutcome) {
      const availableOutcomes = matchingMarket.outcomes.map((o: any) => o.text);
      throw new Error(
        `Matching outcome "${originalBetLabel}" not found in market "${matchingMarket.name}" for related game "${relatedGame.name}". Available outcomes: ${availableOutcomes.join(', ')}`
      );
    }

    return {
      market: matchingMarket,
      marketGroup: matchingMarketGroup,
      outcome: matchingOutcome,
      gameDetail: relatedGameDetail,
      validDraws,
    };
  };

  const createRelatedBet = (
    matchingResult: MatchingResult,
    originalBet: BetslipItem
  ): BetslipItem => {
    const { market, marketGroup, outcome, gameDetail, validDraws } = matchingResult;

    const deriveSelectedDraws = () => {
      if (!Array.isArray(validDraws) || validDraws.length === 0) {
        return [];
      }

      const originalSelectedDraws = Array.isArray(originalBet.draws) ? originalBet.draws : [];
      if (originalSelectedDraws.length === 0) {
        return validDraws.slice(0, Math.min(originalBet.drawCount || 1, validDraws.length));
      }

      const sourceDraws = Array.isArray(originalBet.allAvailableDraws) && originalBet.allAvailableDraws.length > 0
        ? originalBet.allAvailableDraws
        : originalSelectedDraws;

      const selected = originalSelectedDraws.map((draw) => {
        const idx = sourceDraws.findIndex((sourceDraw: any) => {
          if (sourceDraw.event_id && draw.event_id) {
            return sourceDraw.event_id === draw.event_id;
          }
          return sourceDraw.draw_date === draw.draw_date;
        });

        if (idx >= 0 && validDraws[idx]) {
          return validDraws[idx];
        }
        return null;
      }).filter(Boolean);

      if (selected.length === 0) {
        const fallbackLength = originalSelectedDraws.length || originalBet.drawCount || 1;
        return validDraws.slice(0, Math.min(fallbackLength, validDraws.length));
      }

      return selected;
    };

    const selectedDraws = deriveSelectedDraws();

    let betType = outcome.text;
    let marketDescription = market.description;
    if (outcome.specifier) {
      betType = outcome.text.replace('{value}', outcome.specifier);
      marketDescription = market.description.replace('{value}', outcome.specifier);
    }

    return {
      market_id: market.id,
      market_group_id: marketGroup.id,
      game_id: gameDetail.id,
      game_name: gameDetail.name,
      draws: selectedDraws,
      allAvailableDraws: validDraws,
      drawCount: selectedDraws.length,
      numbers: originalBet.numbers,
      stake: originalBet.stake,
      betType: betType,
      maxStake: Number(gameStore.getBetSettings?.bet_limit) ?? 10000,
      odds: Number(outcome.odds),
      numBets: originalBet.numBets || 1,
      picks: originalBet.picks,
      outcome_id: outcome.id.toString(),
      label: betType,
      market_name: market.name,
      market_description: marketDescription,
      specifier: outcome.specifier,
      currency_symbol: gameStore.getBetSettings?.currency_symbol,
    };
  };

  return {
    findMatchingBet,
    createRelatedBet,
  };
}

